import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import api from "../../services/httpService";

const buttonClass = "inline-block rounded-md bg-red-600 px-5 py-3 text-white font-semibold hover:bg-red-700 disabled:opacity-50";

function RecoveryCard({ children }) {
  return <main className="min-h-[60vh] bg-gray-50 px-4 py-12 sm:py-16">
    <div className="mx-auto w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">{children}</div>
  </main>;
}

export function InactiveCustomer() {
  const { state } = useLocation();
  const notice = state?.reactivation;
  if (notice?.code !== "customer_reactivation_required") return <Navigate to="/login" replace />;
  return <RecoveryCard>
    <h1 className="text-2xl font-bold text-gray-900">{notice.title}</h1>
    <p className="mt-4 break-words text-gray-600" role="status">{notice.message}</p>
    {notice.delivery === "sent" && <p className="mt-4 text-gray-600">If you don’t see the email, check your spam or junk folder.</p>}
    <div className="mt-6"><Link className={buttonClass} to="/login">Back to sign in</Link></div>
    <p className="mt-5 text-sm text-gray-600">Need help? <a className="underline" href="mailto:ops@livetakeoff.com">Contact support</a>.</p>
  </RecoveryCard>;
}

function errorMessages(error) {
  const messages = error.response?.data?.errors;
  return Array.isArray(messages) && messages.every(value => typeof value === "string")
    ? messages : ["We couldn’t complete your request. Please try again."];
}

export default function CustomerReactivation() {
  const { uid, token } = useParams();
  const [phase, setPhase] = useState("checking");
  const [requirements, setRequirements] = useState([]);
  const [errors, setErrors] = useState([]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Keep recovery URLs out of referrers when following links from this page.
    const referrer = document.createElement("meta");
    referrer.name = "referrer";
    referrer.content = "no-referrer";
    document.head.appendChild(referrer);
    setPhase("checking");
    setErrors([]);
    api.post("/api/account-reactivation", { action: "validate", user_id: uid, token })
      .then(({ data }) => {
        if (!cancelled) { setRequirements(data.password_requirements); setPhase("ready"); }
      }).catch(error => {
        if (!cancelled) { setErrors(errorMessages(error)); setPhase("invalid"); }
      });
    return () => { cancelled = true; referrer.remove(); };
  }, [uid, token]);

  async function submit(event) {
    event.preventDefault();
    setErrors([]);
    setSaving(true);
    try {
      await api.post("/api/account-reactivation", {
        action: "complete", user_id: uid, token,
        new_password: password, confirm_password: confirmPassword,
      });
      setPassword("");
      setConfirmPassword("");
      setPhase("complete");
    } catch (error) { setErrors(errorMessages(error)); }
    finally { setSaving(false); }
  }

  return <RecoveryCard>
    <h1 className="text-2xl font-bold text-gray-900">{phase === "complete" ? "Your account is reactivated" : "Reactivate your account"}</h1>
    {phase === "checking" && <p className="mt-4 text-gray-600" role="status">Checking your reactivation link…</p>}
    {errors.length > 0 && <div className="mt-4 rounded-md bg-red-50 p-4 text-red-700" role="alert">{errors.map((message, index) => <p key={index}>{message}</p>)}</div>}
    {phase === "complete" && <>
      <p className="mt-4 text-gray-600" role="status">Your new password has been saved. Sign in to access LiveTakeoff.</p>
      <div className="mt-6"><Link to="/login" className={buttonClass}>Sign in</Link></div>
    </>}
    {phase === "invalid" && <div className="mt-6"><Link to="/login" className={buttonClass}>Back to sign in</Link></div>}
    {phase === "ready" && <>
      <p className="mt-4 text-gray-600">Create a new password to reactivate your account.</p>
      <ul id="password-requirements" className="mt-4 list-disc space-y-1 pl-5 text-sm text-gray-600">
        {requirements.map(rule => <li key={rule}>{rule}</li>)}
      </ul>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div><label htmlFor="reactivation-password" className="block text-sm font-medium text-gray-700">New password</label>
          <input id="reactivation-password" type={showPassword ? "text" : "password"} autoComplete="new-password" required maxLength={1024}
            aria-describedby="password-requirements" value={password} onChange={event => setPassword(event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 focus:border-red-500 focus:ring-red-500" /></div>
        <div><label htmlFor="reactivation-confirm" className="block text-sm font-medium text-gray-700">Confirm new password</label>
          <input id="reactivation-confirm" type={showPassword ? "text" : "password"} autoComplete="new-password" required maxLength={1024}
            value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 focus:border-red-500 focus:ring-red-500" /></div>
        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={showPassword} onChange={event => setShowPassword(event.target.checked)} />Show passwords</label>
        <button type="submit" className={`${buttonClass} w-full`} disabled={saving}>{saving ? "Reactivating…" : "Save password and reactivate"}</button>
      </form>
    </>}
  </RecoveryCard>;
}
